import React, { useState } from 'react'
import _cx from 'classnames/bind'
import CardContainer from '../../../cardContainer'
import Button from '../../../button'
import Text from '../../../text'
import { ColorNames } from '../../../../../constants/colorVars'
import type { MarkdownComponentBaseProps, ConfirmAction } from '../../types'
import cls from './styles.scss'

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const cx = _cx.bind(cls)

export interface MarkdownConfirmProps extends MarkdownComponentBaseProps {
  question: string
  yesLabel: string
  yesPrompt: string
  noLabel: string
  noPrompt: string
}

/**
 * A confirmation component rendered from ```ui:confirm blocks.
 * Displays a question with yes/no action buttons inside a CardContainer.
 * After a button is clicked, both buttons disable and the selected one
 * shows a "selected" visual state.
 */
const MarkdownConfirm: React.FC<MarkdownConfirmProps> = ({
  question,
  yesLabel,
  yesPrompt,
  noLabel,
  noPrompt,
  onAction,
}) => {
  const [selection, setSelection] = useState<'yes' | 'no' | null>(null)

  const handleClick = (confirmed: boolean) => {
    if (selection !== null) return // already actioned

    const chosen = confirmed ? 'yes' : 'no'
    setSelection(chosen)

    const action: ConfirmAction = {
      type: 'confirm',
      prompt: confirmed ? yesPrompt : noPrompt,
      confirmed,
    }
    onAction(action)
  }

  return (
    <div className={cx('markdown-confirm')}>
      <CardContainer
        borderStyle='line'
        borderColor={ColorNames.GrayLight}
        borderRadius='medium'
        padding='medium'
        elmId='markdown-confirm'
      >
        <div className={cx('content')}>
          <Text size='small' color='grayDark' className={cx('question')}>
            {question}
          </Text>
          <div className={cx('actions')}>
            <Button
              role='secondary'
              color={ColorNames.Gray}
              onClick={() => handleClick(false)}
              disabled={selection !== null}
              isSelected={selection === 'no'}
              elmId='markdown-confirm-no'
            >
              {noLabel}
            </Button>
            <Button
              role='primary'
              onClick={() => handleClick(true)}
              disabled={selection !== null}
              isSelected={selection === 'yes'}
              elmId='markdown-confirm-yes'
            >
              {yesLabel}
            </Button>
          </div>
        </div>
      </CardContainer>
    </div>
  )
}

export default MarkdownConfirm
